import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/alert-dialog/alert-dialog';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { deleteAdminMutationOptions } from '@/features/admins/api/delete-admin.api';
import { GET_LIST_ADMIN_QUERY_KEY } from '@/features/admins/api/get-list-admin.api';

export interface DeleteAdminDialogProps extends BaseDialogProps {
  adminId: string;
}

export default function DeleteAdminDialog({
  dialog,
  adminId,
}: DeleteAdminDialogProps) {
  const { closeDeleteAdminDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const deleteAdminMutation = useMutation({
    ...deleteAdminMutationOptions(),
    onSuccess: () => {
      toast.success('Admin berhasil dihapus');
      queryClient.invalidateQueries({ queryKey: [GET_LIST_ADMIN_QUERY_KEY] });
      closeDeleteAdminDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal menghapus admin';
      toast.error(message);
    },
  });

  const isPending = deleteAdminMutation.isPending;

  return (
    <AlertDialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState && !isPending) {
          closeDeleteAdminDialog();
        }
      }}
    >
      <AlertDialogContent showCloseButton={false}>
        <AlertDialogHeader>
          <AlertDialogTitle>Hapus admin ini?</AlertDialogTitle>
          <AlertDialogDescription>
            Tindakan ini tidak dapat dibatalkan. Data admin akan dihapus secara
            permanen dari sistem.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Batal</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={isPending}
            onClick={() => deleteAdminMutation.mutate({ id: adminId })}
          >
            {isPending ? 'Menghapus...' : 'Hapus'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
